/* eslint-disable max-lines */
import React, { useState } from "react"
import tw, { styled, css } from "twin.macro"
import { Button, ButtonTheme } from "../formElements/button"
import { ReactComponent as ArrowLeftPrimary } from "../../assets/icons/arrow-left-primary.svg"
import { useTranslation } from "next-i18next"
import { AskQuestionProps, AskQuestionStep, SupportPaymentProps } from "."
import { MockCardCvcElement, MockCardExpiryElement, MockCardNumberElement } from "../paymentGate/mocks"
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { Checkbox } from "../formElements/checkbox"
import { CardBrandIcon } from "../formElements/creditCard"
import {
  AuthRoleType,
  Maybe,
  useCreateSupportPaymentMutation,
  useUpdateUserSetSavePaymentMethodMutation,
} from "../../graphql/generated/graphql"
import { Events, registerEvent } from "../../analytics"
import { Tooltip, TooltipPosition } from "../formElements/tooltip"
import { ReactComponent as InfoIcon } from "../../assets/icons/info.svg"
import { useEffectAsync } from "../../utils/hooks/useEffectAsync"
import { StripePaymentRequestButtonElement } from "@stripe/stripe-js"

const StyledPaymentForm = styled.div(() => [
  tw`space-y-2`,
  css`
    input::placeholder {
      ${tw`text-2xs text-base-4`}
    }
    .cardElementField {
      padding: 13px 5px;
      width: 22%;
      ${tw`h-11 text-sm`}
    }
    .cardElementField:nth-child(1) {
      ${tw`w-full`}
    }
    .cardElementField:nth-child(2) {
      width: 37%;
    }
  `,
])

interface CreditCardProps {
  isStaff: boolean
  isFaux?: boolean
}
const CreditCard = styled.div(({ isStaff, isFaux }: CreditCardProps) => [
  tw`w-full border border-base-3 rounded text-base-6 flex justify-between items-center`,
  isStaff && tw`border-base-1`,
  isFaux && tw`px-2`,
  isFaux && isStaff && tw`text-base-3`,
])

interface QuestionPriceProps {
  isStaff: boolean
  price: Maybe<number> | undefined
  name: Maybe<string> | undefined
}
const QuestionPrice = ({ price, isStaff, name }: QuestionPriceProps) => {
  const { t } = useTranslation("profile")
  if (isStaff) {
    return (
      <div tw="space-y-4">
        {!!price && price > 0 && (
          <p tw="px-2.5 py-1 rounded-full bg-green text-white text-xs font-bold inline-block">
            {t("ask_question.bonsai_discount")}
          </p>
        )}
        <p tw="font-bold flex justify-between text-lg">
          <span>{t("ask_question.total")}</span>

          <span id="price" tw="flex items-center space-x-1" css="svg{margin-left:5px;}">
            <span data-testid="discounted-price">$0.00</span>
            <span
              tw="text-base-4 line-through"
              css="text-decoration-thickness: 2px; text-decoration-color: #ff00aa;"
              data-testid="price"
            >
              ${price}.00
            </span>

            <Tooltip
              text={<InfoIcon />}
              tooltipText={t("ask_question.price_info", { artist: name, price })}
              tooltipLeft={-20}
              position={TooltipPosition.Left}
            />
          </span>
        </p>
      </div>
    )
  }
  return (
    <p tw="font-bold flex justify-between text-lg">
      <span>{t("ask_question.total")}</span>
      <span id="price" tw="flex items-center space-x-1" css="svg{margin-left:5px;}">
        <span data-testid="price">${price}.00</span>
        <Tooltip
          text={<InfoIcon />}
          tooltipText={t("ask_question.price_info", { artist: name, price })}
          tooltipLeft={-20}
          position={TooltipPosition.Left}
        />
      </span>
    </p>
  )
}

export const SupportPayment = (props: AskQuestionProps & SupportPaymentProps) => {
  const { t } = useTranslation("profile")
  const {
    User,
    profile,
    questionBody,
    refetchProfileData,
    createQuestion,
    setStep,
    setQuestionId,
    setShowPaymentGate,
    storybookError,
  } = props

  const { price, name, authRole, isTakingQuestions } = profile

  const stripe = useStripe()
  const elements = useElements()
  const [updatePaymentMethod] = useUpdateUserSetSavePaymentMethodMutation()
  const [createSupportPayment] = useCreateSupportPaymentMutation()
  const isFree = price === 0 || !price
  const isStaff = authRole === AuthRoleType.Staff
  const [errorMessage, setErrorMessage] = useState<string>(storybookError ?? "")
  const [paymentButton, setPaymentButton] = useState<StripePaymentRequestButtonElement | null>(null)
  const [savePaymentMethod, toggleSavePaymentMethod] = useState(!isFree) // when is free the User doesn't go through payment page so we won't have a payment to save
  const [isProcessing, setProcessing] = useState(false)

  const isStripeTestMode = process.env.ENV === "testing"
  const className = "cardElementField"
  const cardNumber = CardNumberElement(isStaff ? { className, options: { disabled: true } } : { className })
  const cardExpiry = CardExpiryElement(isStaff ? { className, options: { disabled: true } } : { className })
  const cardCvc = CardCvcElement(isStaff ? { className, options: { disabled: true } } : { className })
  const userPaymentMethodId = User.savedPaymentMethod?.paymentMethodId
  const last4 = User.savedPaymentMethod?.last4
  const cardType = User.savedPaymentMethod?.cardType ?? ""
  const isPaymentGate = !!setShowPaymentGate

  useEffectAsync(async () => {
    // this loads the new stripe payment request button
    // PaymentButton is gated to ADMIN Users only for now!
    if (elements && stripe && !paymentButton && User.authRole === AuthRoleType.Admin) {
      console.log("** attempting to create payment button ", paymentButton)
      const paymentRequest = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "question total",
          amount: price ? price * 100 : 0,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })

      const prButton = elements.create("paymentRequestButton", {
        paymentRequest,
      })

      const canMakePayment = await paymentRequest.canMakePayment()
      console.log("*** canMakePayment ", canMakePayment)
      const payElement = document.getElementById("payment-request-button")
      if (canMakePayment && payElement?.hasChildNodes() === false) {
        prButton.mount("#payment-request-button")
        setPaymentButton(prButton)
      } else {
        //TODO decide if we should do something special in this case?
      }

      paymentRequest.on("paymentmethod", async (ev) => {
        // Confirm the PaymentIntent without handling potential next actions (yet).
        const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
          User.stripeSetupIntentClientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false },
        )

        if (confirmError) {
          // Report to the browser that the payment failed, prompting it to
          // re-show the payment interface, or show an error message and close
          // the payment interface.
          ev.complete("fail")
        } else {
          // Report to the browser that the confirmation was successful, prompting
          // it to close the browser payment method collection interface.
          ev.complete("success")
          // Check if the PaymentIntent requires any actions and if so let Stripe.js
          // handle the flow. If using an API version older than "2019-02-11"
          // instead check for: `paymentIntent.status === "requires_source_action"`.
          if (paymentIntent && paymentIntent.status === "requires_action") {
            // Let Stripe.js handle the rest of the payment flow.
            const { error } = await stripe.confirmCardPayment(User.stripeSetupIntentClientSecret)
            if (error) {
              // The payment failed -- ask your customer for a new payment method.
              console.log("error - requires_action", error)
            } else {
              // The payment has succeeded.
              console.log("payment success - no action required")
            }
          } else {
            // The payment has succeeded.
            console.log("payment success")
          }
        }
      })
    } else if (!!paymentButton) {
      // if the button exists already make it visible by mounting it in the DOM
      paymentButton.mount("#payment-request-button")
    } else {
      //console.log("payment button not created")
    }
  }, [elements, stripe])

  const handleSubmit = async (paymentMethodId: string) => {
    let questionId
    const response = await createQuestion?.({ variables: { body: questionBody, artistId: profile.id } })

    // remove the sessionQuestion upon submitting a question
    sessionStorage.removeItem("sessionQuestion")
    if (!!response && response.data?.CreateQuestion.success) {
      questionId = response.data.CreateQuestion.question?.id
    }
    if (questionId && price) {
      registerEvent(Events.SubmittedCreateQuestionWithPayment)
      await createSupportPayment({
        variables: { questionId, price, paymentMethodId, savePaymentMethod },
      })
      setQuestionId?.(questionId)
      setStep?.(AskQuestionStep.ThankYou)
    }
    setShowPaymentGate?.(false)
  }

  const handleFreeQuestion = async () => {
    let questionId
    const coupon = isStaff ? "ABCD1234" : undefined
    const response = await createQuestion?.({ variables: { body: questionBody, artistId: profile.id } })
    // remove the sessionQuestion upon submitting a question
    sessionStorage.removeItem("sessionQuestion")
    if (!!response && response.data?.CreateQuestion.success) {
      questionId = response.data.CreateQuestion.question?.id
    }
    if (questionId) {
      registerEvent(Events.SubmittedCreateQuestionWithPayment)
      await createSupportPayment({
        variables: {
          questionId,
          price: 0,
          paymentMethodId: undefined,
          savePaymentMethod: false,
          couponCode: coupon,
        },
      })
      setQuestionId?.(questionId)
      setStep?.(AskQuestionStep.ThankYou)
    }
    setShowPaymentGate?.(false)
  }
  const handlePayment = async () => {
    setProcessing(true)
    if (!!userPaymentMethodId && !isPaymentGate) {
      await handleSubmit(userPaymentMethodId)
    } else {
      const card = elements?.getElement(CardNumberElement)
      // CardNumberElement won't exist in the DOM when price is 0
      if (!stripe || !elements || !card || isStripeTestMode) {
        return
      }
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card,
        billing_details: {
          name: User.name || "",
          email: User.email,
        },
      })
      setErrorMessage(error?.message || "")
      if (paymentMethod) {
        // now that we have a valid payment method id, confirm the card set up combining the payment method to the customer via set up intent
        const { setupIntent, error } = await stripe.confirmCardSetup(User.stripeSetupIntentClientSecret, {
          payment_method: paymentMethod.id,
        })
        setErrorMessage(error?.message || "")
        if (setupIntent && setupIntent.payment_method) {
          const paymentMethodId = setupIntent.payment_method as string
          await updatePaymentMethod({
            variables: { paymentMethodId, savePaymentMethod },
          })
          await handleSubmit(paymentMethodId)
        }
      }
    }
    setProcessing(false)
  }
  const handleRemovePaymentMethod = async () => {
    setProcessing(true)
    await updatePaymentMethod({ variables: { paymentMethodId: "", savePaymentMethod: false } })
    await refetchProfileData?.()
    setProcessing(false)
  }
  const renderPaymentInput =
    userPaymentMethodId && !isPaymentGate ? (
      <CreditCard isFaux={true} isStaff={isStaff}>
        <CardBrandIcon cardBrand={cardType} isStaff={isStaff} />
        <p tw="text-center text-sm" data-testid="last4">
          &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; {last4}
        </p>
        <Button
          twStyle={tw`py-2 px-6 text-md tablet-lg:(px-4 py-1 text-xs)`}
          theme={ButtonTheme.WarningGhost}
          onClickAsync={async (e: React.MouseEvent) => {
            e.preventDefault()
            await handleRemovePaymentMethod()
          }}
          isDisabled={isProcessing || isStaff}
          action="delete"
          dataTestId="delete-saved-payment"
        />
      </CreditCard>
    ) : (
      <div tw="space-y-0" data-testid="creditcard">
        <CreditCard isFaux={false} isStaff={isStaff}>
          {isStripeTestMode ? MockCardNumberElement({}) : cardNumber}
          {isStripeTestMode ? MockCardExpiryElement({}) : cardExpiry}
          {isStripeTestMode ? MockCardCvcElement({}) : cardCvc}
        </CreditCard>
        <Checkbox
          handleChange={() => toggleSavePaymentMethod(!savePaymentMethod)}
          checked={savePaymentMethod}
          label={t("payment.checkbox_label")}
          name="save-payment"
          testId="save-payment-checkbox"
          disabled={isProcessing || isStaff}
        />
        <div id="payment-request-button"></div>
      </div>
    )
  return (
    <form tw="space-y-4 w-full" data-testid="support-payment">
      <h3 tw="title-3" css={[isPaymentGate && tw`hidden`]}>
        {t("ask_question.question_submit")}
      </h3>
      <div css={[isPaymentGate && tw`hidden`]}>
        <p tw="font-bold text-md">{t("ask_question.payment_prompt", { artist: name })}</p>
        <blockquote tw="text-sm py-2 px-4">{questionBody}</blockquote>
      </div>

      <StyledPaymentForm
        css={[(isFree || isStaff) && tw`hidden`]}
        data-testid={isStaff ? `payment-form-discounted` : `payment-form`}
      >
        <div>
          <p tw="font-bold">{t("payment.payment_method")}</p>
          {errorMessage && <p tw="text-red text-xs font-medium">{errorMessage}</p>}
        </div>
        {renderPaymentInput}
      </StyledPaymentForm>
      {isTakingQuestions && <QuestionPrice price={price} isStaff={isStaff} name={name} />}
      {(!isStaff || !isFree) && (
        <p tw="text-xs text-center font-medium text-base-6" css={[(isFree || isPaymentGate) && tw`hidden`]}>
          {t("ask_question.payment_note")}&nbsp;
          <span tw="font-light">{t("ask_question.notification_disclaimer")}</span>
        </p>
      )}

      <div tw="pt-2 flex justify-between">
        {!isPaymentGate && (
          <Button
            theme={ButtonTheme.PrimaryGhost}
            icon={<ArrowLeftPrimary />}
            onClick={() => {
              setStep?.(AskQuestionStep.QuestionForm)
            }}
            isDisabled={isProcessing}
          />
        )}
        <Button
          action="Submit"
          dataTestId="payment-submit"
          twStyle={isPaymentGate ? tw`w-full` : undefined}
          onClickAsync={isFree || isStaff ? handleFreeQuestion : handlePayment}
        />
      </div>
    </form>
  )
}
